// src/components/Seller/SellerPayouts.js
import React, { useState, useEffect, useMemo } from "react";
import "../../styles/SellerPayouts.css";
import config from "../../config";

export default function SellerPayouts({ sellerId }) {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!sellerId) return;
    fetchPayouts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerId]);

  const fetchPayouts = async () => {
    setLoading(true);
    setError("");

    try {
      // ✅ use backend seller endpoint
      const res = await fetch(
        `${config.baseUrl}/api/seller/payouts/${sellerId}`,
        {
          headers: {
            Accept: "application/json",
          },
        }
      );

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to fetch payouts");
      }

      const data = await res.json();
      // backend returns a plain list: List<SellerPayoutDTO>
      setPayouts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching payouts for seller:", err);
      setError(err.message || "Error loading payouts");
    } finally {
      setLoading(false);
    }
  };

  // ---------- helpers ----------
  const formatCurrency = (value) => {
    const num = Number(value || 0);
    return `₹${num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("en-IN");
  };

  const formatPeriod = (start, end) => {
    if (!start || !end) return "-";
    return `${formatDate(start)} – ${formatDate(end)}`;
  };

  // ---------- totals from list ----------
  const { totalEarnings, pendingAmount } = useMemo(() => {
    let total = 0;
    let pending = 0;

    payouts.forEach((p) => {
      const amt = Number(p.sellerEarning || 0);
      total += amt;
      if (p.status !== "PAID") {
        pending += amt;
      }
    });

    return { totalEarnings: total, pendingAmount: pending };
  }, [payouts]);

  return (
    <div className="seller-payouts-container">
      <h3>💰 Seller Payouts</h3>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="card">
          <p className="label">Total Earnings</p>
          <h2 className="amount">{formatCurrency(totalEarnings)}</h2>
        </div>
        <div className="card pending">
          <p className="label">Pending Amount</p>
          <h2 className="amount">{formatCurrency(pendingAmount)}</h2>
        </div>
      </div>

      {loading && <p className="loading">Loading payouts...</p>}
      {error && <div className="error-message">{error}</div>}

      {!loading && !error && payouts.length === 0 && (
        <div className="empty-state">
          <p>💳 No payout history yet</p>
        </div>
      )}

      {payouts.length > 0 && (
        <div className="payouts-table">
          <table>
            <thead>
              <tr>
                <th>Payout ID</th>
                <th>Period</th>
                <th>Gross</th>
                <th>Commission</th>
                <th>Seller Earning</th>
                <th>Status</th>
                <th>Payment Ref</th>
                <th>Paid At</th>
              </tr>
            </thead>
            <tbody>
              {payouts.map((p) => (
                <tr key={p.id}>
                  <td>{p.id}</td>
                  <td>{formatPeriod(p.periodStart, p.periodEnd)}</td>
                  <td>{formatCurrency(p.grossAmount)}</td>
                  <td>{formatCurrency(p.commissionAmount)}</td>
                  <td className="amount">{formatCurrency(p.sellerEarning)}</td>
                  <td>
                    <span
                      className={`status-chip ${
                        p.status === "PAID" ? "paid" : "generated"
                      }`}
                    >
                      {p.status}
                    </span>
                  </td>
                  <td>{p.paymentReference || "-"}</td>
                  <td>{formatDate(p.paidAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
